import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Checkbox, CheckboxGroup, Column, Grid, Typography } from '@tempmee/react';
import { SectionEditDialog } from '~/components/shared/AccountArea';
import {
  buildCredentialsPatch,
  mergeXraySoftware,
  requiredOk,
  splitXraySoftware,
  toSectionDefaults,
  useUpdateLocationCredentials,
  type IInsuranceSoftwareValues,
} from '~/domain/LocationSettings';
import type { IUserCredentials } from '~/models/UserCredentials';
import { useOfficeEducationOptions } from '../hooks/useOfficeEducationOptions';
import { RequireCredentials } from './RequireCredentials';
import type { ISectionModalProps } from './types';

const TITLE = 'Edit Insurance & Software';
const DATA_CY = 'edit-insurance-software';

interface IPickerProps {
  label: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  dataCy: string;
}

const Picker = ({ label, options, value, onChange, dataCy }: IPickerProps) => (
  <Column gap={2} data-cy={dataCy}>
    <Typography variant="body1" bold>
      {label}
    </Typography>
    <CheckboxGroup value={value} onChange={(next) => onChange([...next].sort())}>
      <Grid columns={3} gap={3}>
        {options.map((name) => (
          <Checkbox key={name} value={name} label={name} />
        ))}
      </Grid>
    </CheckboxGroup>
  </Column>
);

type TInsuranceSoftwareFormProps = ISectionModalProps & { credentials: IUserCredentials };

const InsuranceSoftwareForm = ({
  officeId,
  office,
  credentials,
  onClose,
  onSaved,
}: TInsuranceSoftwareFormProps) => {
  const options = useOfficeEducationOptions();
  const sections = useMemo(() => toSectionDefaults(office, credentials), [office, credentials]);
  const { control, handleSubmit, formState } = useForm<IInsuranceSoftwareValues>({
    defaultValues: sections.insuranceSoftware,
    mode: 'onChange',
  });
  const values = useWatch({ control }) as IInsuranceSoftwareValues;
  const { dirtyFields } = formState;
  const updateCredentials = useUpdateLocationCredentials(officeId);

  const softwareOptions = useMemo(() => splitXraySoftware(options.softwares), [options.softwares]);

  const canSave =
    formState.isDirty &&
    requiredOk(!!dirtyFields.insurances, values.insurances.length > 0) &&
    requiredOk(
      !!(dirtyFields.softwares || dirtyFields.xraySoftwares),
      values.softwares.length + values.xraySoftwares.length > 0,
    );

  // The specialty rides along unchanged: the PATCH replaces the whole object.
  const save = handleSubmit(async (submitted) => {
    await updateCredentials.mutateAsync(
      buildCredentialsPatch({
        specialty: sections.basicInformation.specialty,
        softwares: mergeXraySoftware(submitted.softwares, submitted.xraySoftwares),
        insurances: submitted.insurances,
      }),
    );
    onSaved();
  });

  return (
    <SectionEditDialog
      title={TITLE}
      dataCy={DATA_CY}
      onClose={onClose}
      onSave={save}
      canSave={canSave}
    >
      <Controller
        name="insurances"
        control={control}
        render={({ field }) => (
          <Picker
            label="Insurance"
            options={options.insurances}
            value={field.value}
            onChange={field.onChange}
            dataCy="location-insurances"
          />
        )}
      />
      <Controller
        name="softwares"
        control={control}
        render={({ field }) => (
          <Picker
            label="Software"
            options={softwareOptions.software}
            value={field.value}
            onChange={field.onChange}
            dataCy="location-softwares"
          />
        )}
      />
      <Controller
        name="xraySoftwares"
        control={control}
        render={({ field }) => (
          <Picker
            label="X-Ray Software"
            options={softwareOptions.xray}
            value={field.value}
            onChange={field.onChange}
            dataCy="location-xray-softwares"
          />
        )}
      />
    </SectionEditDialog>
  );
};

/** Insurance, software and X-ray software (split out of the one software list). */
export const InsuranceSoftwareModal = (props: ISectionModalProps) => (
  <RequireCredentials
    title={TITLE}
    dataCy={DATA_CY}
    credentials={props.credentials}
    onClose={props.onClose}
  >
    {(credentials) => <InsuranceSoftwareForm {...props} credentials={credentials} />}
  </RequireCredentials>
);
